import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/base/PageLayout';
import HubCards from '../../components/feature/HubCards';

const Media: React.FC = () => {
  useEffect(() => {
    document.title = 'メディア出演・取材のご依頼 - テレビ・雑誌・執事監修依頼｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルのメディア出演・取材のご依頼について。テレビ・ラジオ・雑誌・ウェブメディア等での出演・取材を承ります。執事文化、マナー、おもてなし、ホスピタリティをテーマとした企画にご協力いたします。全日本執事協会頭取として培った知識と経験を活かしたメディア対応をいたします。');
    }

    // Add Keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'メディア出演, 取材依頼, テレビ出演, 雑誌掲載, 執事監修');
    } else {
      const keywordsTag = document.createElement('meta');
      keywordsTag.name = 'keywords';
      keywordsTag.content = 'メディア出演, 取材依頼, テレビ出演, 雑誌掲載, 執事監修';
      document.head.appendChild(keywordsTag);
    }

    // Add Canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/media`);
    } else {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/media`;
      document.head.appendChild(canonical);
    }

    // Add Last-modified
    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    } else {
      const lastModTag = document.createElement('meta');
      lastModTag.name = 'last-modified';
      lastModTag.content = new Date().toISOString().split('T')[0];
      document.head.appendChild(lastModTag);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "メディア出演・取材のご依頼",
      "description": "執事ベルのテレビ出演、雑誌掲載、書籍出版、ラジオ出演などのメディア実績一覧。執事監修や取材のご依頼も承ります。",
      "url": `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/media`,
      "about": {
        "@type": "Person",
        "name": "執事ベル",
        "jobTitle": "全日本執事協会 頭取"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <PageLayout hero={<>
<section className="relative py-16 md:py-32">
          <div
            className="absolute inset-0 bg-cover bg-center"
            role="img"
            aria-label="夜の執事サロンの正餐の間にある長いテーブルとシャンデリア"
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('/images/salon0926/DSC00335.jpg')`
            }}
          ></div>
          <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <h1 className="text-2xl md:text-6xl font-serif mb-4 md:mb-6">
                メディア出演・取材のご依頼
              </h1>
              <p className="text-sm md:text-2xl max-w-6xl mx-auto leading-relaxed px-5 sm:px-6 lg:px-8">
                執事文化、マナー、おもてなしに関する<br className="hidden md:inline" />
                企画・取材にご協力いたします
              </p>
            </div>
          </div>
        </section>
</>}>
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
        {/* Hero Section */}
        

        {/* Overview */}
        <section className="py-12 md:py-20">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-4 md:mb-6">
                メディア出演について
              </h2>
              <p className="text-sm md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-8 px-5 sm:px-6 lg:px-8">
                テレビ・ラジオ・雑誌・新聞・ウェブメディア等での<br className="hidden md:inline" />
                出演・取材のご依頼を承っております
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 lg:p-12">
              <h3 className="text-base md:text-2xl font-serif text-gray-900 mb-4 md:mb-6 text-center">
                対応可能なテーマ
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-12">
                {[
                  {
                    icon: 'ri-user-star-line',
                    title: '執事文化',
                    description: '日本における執事文化の普及と発展について'
                  },
                  {
                    icon: 'ri-hand-heart-line',
                    title: 'おもてなし',
                    description: '日本のおもてなし精神と西洋の執事文化の融合'
                  },
                  {
                    icon: 'ri-book-open-line',
                    title: 'マナー・エチケット',
                    description: 'テーブルマナー、ビジネスマナー、社交マナー'
                  },
                  {
                    icon: 'ri-building-line',
                    title: 'ホスピタリティ',
                    description: 'サービス業における接客品質向上'
                  }
                ].map((theme, index) => (
                  <div key={index} className="flex items-start p-3 md:p-4 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-amber-100 rounded-full mr-3 md:mr-4 flex-shrink-0">
                      <i className={`${theme.icon} text-base md:text-xl text-amber-700`}></i>
                    </div>
                    <div>
                      <h4 className="text-sm md:text-lg font-serif text-gray-900 mb-1 md:mb-2">{theme.title}</h4>
                      <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{theme.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-6 md:pt-8">
                <h3 className="text-base md:text-2xl font-serif text-gray-900 mb-4 md:mb-6 text-center">
                  対応可能なメディア形式
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-12">
                  {[
                    { icon: 'ri-tv-line', label: 'テレビ' },
                    { icon: 'ri-radio-line', label: 'ラジオ' },
                    { icon: 'ri-newspaper-line', label: '新聞・雑誌' },
                    { icon: 'ri-global-line', label: 'ウェブメディア' }
                  ].map((media, index) => (
                    <div key={index} className="text-center p-3 md:p-4 bg-amber-50 rounded-lg">
                      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mx-auto mb-2 md:mb-3 bg-white rounded-full">
                        <i className={`${media.icon} text-xl md:text-2xl text-amber-700`}></i>
                      </div>
                      <div className="text-xs md:text-sm font-medium text-gray-900">{media.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 md:pt-8">
                <h3 className="text-base md:text-2xl font-serif text-gray-900 mb-4 md:mb-6 text-center">
                  お問い合わせ方法
                </h3>
                
                <div className="bg-amber-50 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed text-center mb-3 md:mb-4 px-2">
                    メディア出演・取材のご依頼は、<br className="md:hidden" />
                    下記のお問い合わせフォームよりご連絡ください
                  </p>
                  <ul className="space-y-2 text-xs md:text-sm text-gray-600 max-w-md mx-auto">
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>企画内容・テーマ</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>出演・取材希望日時</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>媒体名・番組名</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-600 mr-2 mt-1 flex-shrink-0"></i>
                      <span>ご担当者様の連絡先</span>
                    </li>
                  </ul>
                </div>

                <div className="text-center">
                  <Link
                    to="/contact"
                    className="btn-gold inline-block bg-amber-700 text-white px-6 md:px-10 py-3 md:py-4 rounded-lg font-medium hover:bg-amber-800 transition-colors whitespace-nowrap text-sm md:text-base"
                  >
                    お問い合わせフォームへ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Info */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 lg:p-12">
              <h3 className="text-base md:text-2xl font-serif text-gray-900 mb-4 md:mb-6 text-center">
                執事ベルについて
              </h3>
              
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-3 md:space-y-4">
                <p className="text-sm md:text-base">
                  全日本執事協会頭取として、日本における執事文化の普及と発展に取り組んでいます。
                </p>
                <p className="text-sm md:text-base">
                  西洋の伝統的な執事文化と日本固有のおもてなし精神を融合させた、新しい執事像を追求しています。執事文化の研究と実践を通じて日々学び続けている知見を、広く共有することを大切にしています。
                </p>
                <p className="text-sm md:text-base">
                  テーブルマナー、ビジネスマナー、ホスピタリティなど、執事文化に関する幅広いテーマでお話しすることが可能です。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-gradient-to-r from-amber-700 to-amber-800">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl md:text-4xl font-serif text-white mb-4 md:mb-6">
              まずはお気軽にご相談ください
            </h2>
            <p className="text-sm md:text-xl text-amber-100 mb-6 md:mb-8 leading-relaxed px-5 sm:px-6 lg:px-8">
              企画内容に応じて柔軟に対応させていただきます
            </p>
            <Link
              to="/contact"
              className="btn-gold inline-block bg-white text-amber-800 px-6 md:px-8 py-3 md:py-4 rounded-lg font-medium hover:bg-amber-50 transition-colors whitespace-nowrap text-sm md:text-base"
            >
              お問い合わせ
            </Link>
          </div>
        </section>
      </div>
          <HubCards group="media" />
    </PageLayout>
  );
};

export default Media;
